import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { filter, isEmpty } from 'lodash'
import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import Folder from '@/public/images/folder.svg'
import type { CompanyWithWorkflowConfig } from '@/schema'

import { CompanyListItem } from './CompanyListItem'
import { CompanySearch } from './CompanySearch'

interface CompanyListProps {
  companies: CompanyWithWorkflowConfig[]
  selectedCompanyId: string | null
  onSelectCompany: (companyId: string) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  onAddReminder: () => void
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  onLoadMore?: () => void
}

export const CompanyList: React.FC<CompanyListProps> = ({
  companies,
  selectedCompanyId,
  onSelectCompany,
  searchQuery,
  onSearchChange,
  onAddReminder,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
}) => {
  const { t } = useTranslation()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const filteredCompanies = filter(companies, (company) =>
    company.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const emptyMessage = searchQuery
    ? t('workflowConfig.noCompaniesFound')
    : t('workflowConfig.noCompanies')

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Spinner className="w-6 h-6 text-blue-600" />
        </div>
      )
    }

    if (isEmpty(filteredCompanies)) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center text-gray-500">
          <img src={Folder} alt="No data" className="h-12 mb-4 w-14" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      )
    }

    return (
      <div>
        {filteredCompanies.map((company) => (
          <CompanyListItem
            key={company.company_id}
            company={company}
            isSelected={selectedCompanyId === company.company_id}
            onClick={() => onSelectCompany(company.company_id)}
          />
        ))}
        {hasNextPage && (
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <Spinner className="w-5 h-5 text-blue-600" />
            )}
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [onLoadMore, hasNextPage, isFetchingNextPage])

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-light-200">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-light-900">
            {t('workflowConfig.company')}
          </h2>
          <Button
            onClick={onAddReminder}
            variant="outlined"
            className="w-10 h-10 p-0 rounded-lg border-gray-light-300 text-gray-light-600 hover:text-gray-light-900 hover:border-gray-light-400"
          >
            <Plus className="w-5 h-5" />
            <span className="sr-only">
              {t('workflowConfig.addCustomReminder')}
            </span>
          </Button>
        </div>
        <CompanySearch
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={t('workflowConfig.searchCompany')}
        />
      </div>

      <div className="flex-1 overflow-y-auto">{renderContent()}</div>
    </div>
  )
}
